import React, { useEffect, useState, useMemo } from "react";
import { Fence, Plus, Search, Edit2, Save, X, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { CardGridSkeleton } from "@/components/LoadingSkeleton";
import { ImageStager } from "@/components/ImageStager";
import { authFetch } from "@/lib/auth";
import { uploadAttachment } from "@/lib/attachments";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import type { Pasture } from "@shared/types";
export function PasturesPage() {
  const [pastures, setPastures] = useState<Pasture[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fName, setFName] = useState("");
  const [fAcreage, setFAcreage] = useState("");
  const [fStatus, setFStatus] = useState("active");
  const [fNotes, setFNotes] = useState("");
  // Staged image attached after the pasture is created.
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [eName, setEName] = useState("");
  const [eAcreage, setEAcreage] = useState("");
  const [eStatus, setEStatus] = useState("active");
  const [eNotes, setENotes] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const fetchPastures = async () => {
    try {
      const data = await authFetch<{ items: Pasture[] }>("/api/pastures");
      setPastures(data.items ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      console.error("[Pastures]", msg);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchPastures(); }, []);
  const filtered = useMemo(() => {
    return pastures.filter((p) => {
      const ms = p.name.toLowerCase().includes(search.toLowerCase());
      const mst = statusFilter === "all" || p.status === statusFilter;
      return ms && mst;
    });
  }, [pastures, search, statusFilter]);
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fName.trim()) { toast.error("Name is required"); return; }
    setSubmitting(true);
    try {
      const created = await authFetch<{ id: string }>("/api/pastures", {
        method: "POST",
        body: JSON.stringify({
          name: fName.trim(), acreage: parseFloat(fAcreage) || 0,
          status: fStatus, animal_count: 0, last_rotation: new Date().toISOString().split("T")[0],
          notes: fNotes.trim(),
        }),
      });

      if (imageFile && created?.id) {
        try {
          await uploadAttachment(imageFile, {
            linked_entity_type: "pasture",
            linked_entity_id: created.id,
          });
        } catch (uploadErr) {
          toast.warning(
            `Saved, but image upload failed: ${(uploadErr as Error).message ?? "unknown error"}`,
          );
        }
      }

      toast.success("Pasture added");
      setFName(""); setFAcreage(""); setFStatus("active"); setFNotes(""); setImageFile(null);
      setDialogOpen(false);
      setLoading(true);
      fetchPastures();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to add");
    } finally {
      setSubmitting(false);
    }
  };
  const startEdit = (p: Pasture) => {
    setEditId(p.id);
    setEName(p.name); setEAcreage(String(p.acreage)); setEStatus(p.status); setENotes(p.notes);
  };
  const handleEditSave = async () => {
    if (!editId) return;
    setEditSaving(true);
    try {
      await authFetch(`/api/pastures/${editId}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: eName.trim(), acreage: parseFloat(eAcreage) || 0,
          status: eStatus, notes: eNotes.trim(),
        }),
      });
      toast.success("Pasture updated");
      setEditId(null);
      setLoading(true);
      fetchPastures();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to update");
    } finally {
      setEditSaving(false);
    }
  };
  const handleArchive = async (id: string) => {
    try {
      await authFetch(`/api/pastures/${id}`, { method: "DELETE" });
      toast.success("Pasture archived");
      setPastures((prev) => prev.filter((p) => p.id !== id));
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to archive");
    }
  };
  return (
    <>
      <PageHeader title="Pastures" breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Pastures" }]}>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
              <Plus className="h-4 w-4 mr-2" /> Add Pasture
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Add Pasture</DialogTitle></DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4 mt-2">
              <div className="space-y-2"><Label>Name *</Label><Input value={fName} onChange={(e) => setFName(e.target.value)} placeholder="North Meadow" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Acreage</Label><Input type="number" step="0.1" value={fAcreage} onChange={(e) => setFAcreage(e.target.value)} placeholder="100" /></div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={fStatus} onValueChange={setFStatus}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="resting">Resting</SelectItem>
                      <SelectItem value="empty">Empty</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Notes</Label><Textarea value={fNotes} onChange={(e) => setFNotes(e.target.value)} rows={2} /></div>
              <div className="space-y-2">
                <Label>Image</Label>
                <ImageStager
                  file={imageFile}
                  onChange={setImageFile}
                  disabled={submitting}
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
                {submitting ? "Adding..." : "Add Pasture"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>
      {error && <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search pastures..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="resting">Resting</SelectItem>
            <SelectItem value="empty">Empty</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {loading ? (
        <CardGridSkeleton count={4} />
      ) : filtered.length === 0 ? (
        <EmptyState icon={Fence} title="No pastures found" description={search || statusFilter !== "all" ? "Try adjusting your filters." : "Add your first pasture to get started."} actionLabel={!search && statusFilter === "all" ? "Add Pasture" : undefined} onAction={!search && statusFilter === "all" ? () => setDialogOpen(true) : undefined} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <Card key={p.id} className="p-5 hover:shadow-md transition-shadow duration-200">
              {editId === p.id ? (
                <div className="space-y-3">
                  <Input value={eName} onChange={(e) => setEName(e.target.value)} placeholder="Name" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" step="0.1" value={eAcreage} onChange={(e) => setEAcreage(e.target.value)} placeholder="Acreage" />
                    <Select value={eStatus} onValueChange={setEStatus}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="resting">Resting</SelectItem>
                        <SelectItem value="empty">Empty</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea value={eNotes} onChange={(e) => setENotes(e.target.value)} rows={2} placeholder="Notes" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleEditSave} disabled={editSaving} className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90 flex-1">
                      <Save className="h-3 w-3 mr-1" /> {editSaving ? "Saving..." : "Save"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditId(null)}><X className="h-3 w-3" /></Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Fence className="h-5 w-5 text-ranch-gold" />
                      <h3 className="font-semibold text-foreground">{p.name}</h3>
                    </div>
                    <StatusBadge status={p.status} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                    <div>
                      <span className="text-muted-foreground">Acreage:</span>{" "}
                      <span className="font-medium">{p.acreage}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Animals:</span>{" "}
                      <span className="font-medium">{p.animal_count}</span>
                    </div>
                  </div>
                  <p className="text-2xs text-muted-foreground mb-3">
                    Last rotation: {p.last_rotation ? format(parseISO(p.last_rotation + "T00:00:00"), "MMM d, yyyy") : "—"}
                  </p>
                  {p.notes && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{p.notes}</p>}
                  <div className="flex gap-2 pt-1 border-t">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(p)} className="text-muted-foreground hover:text-foreground">
                      <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Archive
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Archive {p.name}?</AlertDialogTitle><AlertDialogDescription>This will hide the pasture from active lists.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleArchive(p.id)} className="bg-destructive text-destructive-foreground">Archive</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </>
              )}
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
